/* «Награды и задания» на React. Разметка, классы и порядок узлов — как у assets/bot-rewards.js (раздел 4), стили — rewards.css (копия bot-rewards.css),
   поэтому кадры совпадают с эталоном прототипа. Движение то же: вкладки — swap, новое на месте — pop, числа — tick, «забрал день» — класс hit у кружка дня.
   Строка задания при смене состояния ставится заново (key по состоянию), как outerHTML прототипа: переходов между состояниями строки нет. */
import { Fragment, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { uiFormat as uf, uiPlural as upl, uiText as ut, useAccountLocale } from '../../i18n';
import { LegacyHtml } from '../../ui/LegacyHtml';
import { Mark, Svg } from '../../ui/Svg';
import { Seg } from '../../ui/Seg';
import { Sticky } from '../../ui/Sticky';
import { LiveContext, usePopOnAppear, useLiveRoot, useTickOnChange } from '../../ui/hooks';
import { pop, swap } from '../../ui/motion';
import { useStore } from '../../state/store';
import { TABS, leftDay, leftWeek, type TabKey, type Task, type TaskState } from './data';
import { icon, getRt } from './runtime';
import { claimDay, setTab, taskTap, toPt } from './actions';
import { srvLoad, srvRetry } from './rewards-server';

const NB = '\u00a0';

/** \u0428\u0430\u0431\u043b\u043e\u043d \u0441\u043b\u043e\u0432\u0430\u0440\u044f \u0441 \u0443\u0437\u043b\u0430\u043c\u0438 \u043d\u0430 \u043c\u0435\u0441\u0442\u0430\u0445 {0}, {1}\u2026: \u043f\u043e\u0440\u044f\u0434\u043e\u043a \u0441\u043b\u043e\u0432 \u0441\u0442\u0440\u043e\u0438\u0442 \u044f\u0437\u044b\u043a \u043f\u0440\u043e\u0444\u0438\u043b\u044f (\u0432\u044b\u0434\u0435\u043b\u0435\u043d\u043d\u043e\u0435 \u0447\u0438\u0441\u043b\u043e \u0438\u043b\u0438 \u0438\u043c\u044f \u043e\u0441\u0442\u0430\u0451\u0442\u0441\u044f \u0443\u0437\u043b\u043e\u043c) */
function fmt(template: string, ...parts: ReactNode[]): ReactNode {
  return ut(template)
    .split(/\{(\d+)\}/)
    .map((s, i) => (i % 2 ? <Fragment key={i}>{parts[Number(s)]}</Fragment> : s));
}

function Ic({ n }: { n: string }) {
  return <Svg svg={icon(getRt(), n)} />;
}

/* ── Серия ── */

function Day({ i }: { i: number }) {
  const { store, c } = getRt();
  const S = store.S;
  const days = c.days();
  const got = i < S.streak;
  const now = !S.today && i === S.streak;
  const big = i === days.length - 1;
  const hit = useHit(got);
  return (
    <span className={'rw-day' + (got ? ' got' : '') + (now ? ' now' : '') + (big ? ' big' : '')} data-day={i}>
      <span className={'k' + (hit ? ' hit' : '')}>{got ? <Ic n="tickS" /> : big ? <Ic n="gift" /> : i + 1}</span>
      <b>+{days[i]}</b>
    </span>
  );
}

/** Кружок дня «выскакивает» (класс hit), когда день забрали на глазах */
function useHit(got: boolean) {
  const was = useRef(got);
  const [hit, setHit] = useState(false);
  useLayoutEffect(() => {
    if (got && !was.current) setHit(true);
    was.current = got;
  }, [got]);
  return hit;
}

function Title() {
  const { c } = getRt();
  const n = c.streakN();
  const cnt = useTickOnChange<HTMLElement>(n);
  return (
    <h3 className="promo-t" id="rwT">
      {n ? (
        <>
          <b id="rwCnt" ref={cnt}>
            {n}
          </b>{' '}
          {upl(n, 'день', 'дня', 'дней')} <span>{ut('подряд')}</span>
        </>
      ) : (
        fmt('Начните {0}', <span>{ut('серию')}</span>)
      )}
    </h3>
  );
}

function Wait() {
  const { c } = getRt();
  const ref = usePopOnAppear<HTMLDivElement>();
  return (
    <div className="rw-wait" role="status" ref={ref}>
      <Ic n="clock" />
      <span>{uf('Завтра +{0} C', c.nextAmt())}</span>
    </div>
  );
}

function Streak() {
  const { store, c } = getRt();
  const S = store.S;
  const DAYS = c.days();
  const dayNow = S.today ? S.streak : S.streak + 1;
  return (
    <section className="promo rw-streak" aria-label={ut('Ежедневная серия')}>
      <div className="promo-h">
        <span className="promo-eb">
          <Ic n="flame" />
          {ut('Ежедневная серия')}
        </span>
        <span className="promo-n" id="rwDayN">
          {uf('День {0} из {1}', dayNow, DAYS.length)}
        </span>
      </div>
      <Title />
      <p className="promo-d">
        {uf('Заходите каждый день: награда растёт, на седьмой день {0} C. Пропуск дня начинает серию заново.', DAYS[DAYS.length - 1])}
      </p>
      <div className="rw-week" id="rwWeek">
        {DAYS.map((_, i) => (
          <Day key={i} i={i} />
        ))}
      </div>
      {S.today ? (
        <Wait />
      ) : (
        <button className="btn rw-claim" type="button" data-rw-claim="" onClick={e => claimDay(e.currentTarget)}>
          <Ic n="gift" />
          <span>{uf('Забрать {0} C', c.dayAmt())}</span>
        </button>
      )}
    </section>
  );
}

/* ── Задания ── */

function TaskIcon({ t }: { t: Task }) {
  return t.svg ? <Mark html={t.svg} /> : <Ic n={t.ic || ''} />;
}

function Progress({ t }: { t: Task }) {
  const { c, BC } = getRt();
  const n = c.goal(t);
  const v = Math.min(c.prog(t), n);
  if (t.sv?.off) return <span>{ut(t.sv.off)}</span>;
  return (
    <span className="rw-pg">
      <span className="bar" role="progressbar" aria-label={ut('Прогресс')} aria-valuemin={0} aria-valuemax={n} aria-valuenow={v}>
        <i style={{ transform: `scaleX(${(v / n).toFixed(3)})` }} />
      </span>
      <span className="n">{uf('{0} из {1}', BC.cNum(v), BC.cNum(n))}</span>
    </span>
  );
}

/** pop того, что сменилось в строке (после её перестановки): «+N C» с галочкой или «Забрать N C» */
function usePopAfter<T extends HTMLElement>(id: string) {
  const r = getRt();
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const sel = r.popAfter.get(id);
    if (!sel) return;
    r.popAfter.delete(id);
    pop(ref.current?.querySelector(sel));
  }, [r, id]);
  return ref;
}

function Right({ t, st }: { t: Task; st: TaskState }) {
  const cNum = getRt().BC.cNum;
  if (st === 'done')
    return (
      <span className="st-pr ok">
        <Ic n="tickS" />+{cNum(t.r)} C
      </span>
    );
  if (st === 'claim') return <span className="rw-take">{uf('Забрать {0} C', cNum(t.r))}</span>;
  return (
    <>
      <span className="st-pr">+{cNum(t.r)} C</span>
      <span className="rt">
        <Ic n="chev" />
      </span>
    </>
  );
}

function TaskRow({ t, st }: { t: Task; st: TaskState }) {
  const r = getRt();
  const ref = usePopAfter<HTMLButtonElement>(t.id);
  /* в серверном режиме задание открывает выбор группы Telegram (actions.ts), подсказка-стекло — только в демо */
  const tip = st === 'todo' && t.go === 'chat' && !t.sv ? { 'data-tipb': r.BC.TIPS.addChat } : {};
  return (
    <button
      className={'st-mr rw-task ' + st}
      type="button"
      data-rw-task={t.id}
      {...tip}
      aria-disabled={st === 'done' || t.sv?.off ? 'true' : undefined}
      ref={ref}
      onClick={e => taskTap(t, e.currentTarget)}
    >
      <span className="ic">
        <TaskIcon t={t} />
      </span>
      <span className="m">
        <b>
          <span className="tt">{ut(t.t)}</span>
        </b>
        {r.c.goal(t) > 1 || t.src === 'act' ? <Progress t={t} /> : <span>{ut(t.s)}</span>}
      </span>
      <Right t={t} st={st} />
    </button>
  );
}

function Challenge({ t, st }: { t: Task; st: TaskState }) {
  const { c, BC } = getRt();
  const ref = usePopAfter<HTMLButtonElement>(t.id);
  const n = c.goal(t);
  const v = Math.min(c.prog(t), n);
  return (
    <button
      className={'rw-ch ' + st}
      type="button"
      data-rw-task={t.id}
      aria-disabled={st === 'done' || t.sv?.off ? 'true' : undefined}
      ref={ref}
      onClick={e => taskTap(t, e.currentTarget)}
    >
      <span className="top">
        <span className="ic">
          <TaskIcon t={t} />
        </span>
        {st === 'done' ? (
          <span className="st-pr ok">
            <Ic n="tickS" />+{BC.cNum(t.r)} C
          </span>
        ) : (
          <span className="st-pr">+{BC.cNum(t.r)} C</span>
        )}
      </span>
      <b>{ut(t.t)}</b>
      <span className="s">{ut(t.s)}</span>
      <span className="ft">
        {st === 'claim' ? (
          <span className="rw-take">{uf('Забрать {0} C', BC.cNum(t.r))}</span>
        ) : (
          <>
            <span className="n">{t.sv?.off ? ut(t.sv.off) : uf('{0} из {1}', v, n)}</span>
            <span
              className="tk"
              hidden={!!t.sv?.off}
              role="progressbar"
              aria-label={ut('Прогресс')}
              aria-valuemin={0}
              aria-valuemax={n}
              aria-valuenow={v}
            >
              {Array.from({ length: n }, (_, i) => (
                <i key={i} className={i < v ? 'on' : undefined} />
              ))}
            </span>
          </>
        )}
      </span>
    </button>
  );
}

function Rows({ k }: { k: 'day' | 'one' | 'fr' }) {
  const { c } = getRt();
  /* сервер может не прислать заданий группы: тихая строка вместо пустой карточки */
  if (!c.tasksOf(k).length) return <p className="rw-none">{ut('Заданий здесь пока нет')}</p>;
  return (
    <div className="st-grp">
      {c.tasksOf(k).map(t => {
        const st = c.stOf(t);
        return <TaskRow key={t.id + st} t={t} st={st} />;
      })}
    </div>
  );
}

function Head({ title, children, id }: { title: string; children: ReactNode; id?: string }) {
  const ref = useTickOnChange<HTMLSpanElement>(id ? children : null);
  return (
    <div className="st-h">
      <b>{title}</b>
      <span className="st-hs2" id={id} ref={ref}>
        {children}
      </span>
    </div>
  );
}

function DayPane() {
  const { c, store } = getRt();
  /* время до сброса считается при монтаже, как у разметки прототипа (и заново на языке профиля при его смене); с сервером — по его времени сброса из снимка */
  const loc = useAccountLocale();
  const left0 = useMemo(() => [leftDay(), leftWeek()], [loc]); // eslint-disable-line react-hooks/exhaustive-deps
  const v = store.S.srv;
  const left = v ? [leftDay(v.dayAt), leftWeek(v.weekAt)] : left0;
  return (
    <>
      <Head title={ut('Задания дня')}>{v && v.dayAt == null ? ut('Время обновления недоступно') : uf('Новые через {0}', left[0])}</Head>
      <Rows k="day" />
      <Head title={ut('Челленджи недели')}>{v && v.weekAt == null ? ut('Время обновления недоступно') : left[1]}</Head>
      <div className="rw-chs">
        {c.tasksOf('week').map(t => {
          const st = c.stOf(t);
          return <Challenge key={t.id + st} t={t} st={st} />;
        })}
      </div>
    </>
  );
}

function OnePane() {
  const { c } = getRt();
  const L = c.tasksOf('one');
  return (
    <>
      <Head title={ut('Первые шаги')} id="rwOneN">
        {L.length ? uf('Выполнено {0} из {1}', c.gotN(L), L.length) : ''}
      </Head>
      <Rows k="one" />
    </>
  );
}

function Track() {
  const { c, TASKS, BC } = getRt();
  const STEPS = TASKS.filter(t => t.src === 'act');
  const a = c.actN();
  const k = STEPS.length;
  return (
    <div className="rw-trk" style={{ '--k': k } as CSSProperties} aria-hidden="true">
      {STEPS.slice(0, -1).map((t, i) => {
        const f = Math.max(0, Math.min(1, (a - (t.n || 0)) / ((STEPS[i + 1].n || 0) - (t.n || 0))));
        return (
          <span key={'l' + i} className="ln" style={{ '--i': i } as CSSProperties}>
            <i style={{ transform: `scaleX(${f.toFixed(3)})` }} />
          </span>
        );
      })}
      {STEPS.map(t => (
        <span key={t.id} className={'nd' + (a >= (t.n || 0) ? ' on' : '')}>
          <span className="k">{t.n}</span>
          <small>+{BC.cNum(t.r)} C</small>
        </span>
      ))}
    </div>
  );
}

function FrPane() {
  const { c, TASKS, BC, ui, store } = getRt();
  const known = !store.S.srv || store.S.srv.friendsKnown;
  const a = c.actN();
  const steps = TASKS.filter(t => t.src === 'act');
  const nx = steps.find(t => a < (t.n || 0));
  const L = c.tasksOf('fr');
  const friendsW = (n: number) => upl(n, 'друг', 'друга', 'друзей');
  return (
    <>
      <div className="rw-fs">
        <div className="h">
          <span className="m">
            <b className="kv">{known ? BC.cNum(a) : '—'}</b>
            <span className="u">{known ? uf('{0} с вами', friendsW(a)) : ut('Прогресс друзей недоступен')}</span>
          </span>
          <button
            className="btn sm rw-inv"
            type="button"
            data-rw-inv=""
            onClick={() => {
              ui.buzz();
              toPt({ smooth: true });
            }}
          >
            <Ic n="usersPlus" />
            <span>{ut('Пригласить')}</span>
          </button>
        </div>
        <p className="rw-fsn">{ut('Засчитываются друзья, которые пришли по вашей ссылке или промокоду и создали первую работу')}</p>
        {known && steps.length > 0 && (
          <>
            <Track />
            <p className="rw-fsd">
              {nx
                ? fmt('Ещё {0} {1} до награды {2}', (nx.n || 0) - a, friendsW((nx.n || 0) - a), <b>+{BC.cNum(nx.r)} C</b>)
                : ut('Все ступени пройдены')}
            </p>
          </>
        )}
      </div>
      <Head title={ut('Награды за друзей')} id="rwFrN">
        {L.length ? uf('Получено {0} из {1}', c.gotN(L), L.length) : ''}
      </Head>
      <Rows k="fr" />
    </>
  );
}

function Pane({ k, on, children }: { k: TabKey; on: boolean; children: ReactNode }) {
  return (
    <div
      className={'rw-pane' + (on ? ' on' : '')}
      id={'rwP-' + k}
      data-pane={k}
      role="tabpanel"
      aria-labelledby={'rwTab-' + k}
      inert={!on}
      aria-hidden={on ? undefined : 'true'}
    >
      {children}
    </div>
  );
}

function Tasks() {
  const r = getRt();
  const S = r.store.S;
  /* вкладка на экране: меняется в середине смены (swap), когда вкладки скрыты; сегмент переключается сразу */
  const [shown, setShown] = useState<TabKey>(S.tab);
  const panes = useRef<HTMLDivElement>(null);
  const onTab = (k: TabKey) =>
    setTab(k, (after: () => void) => {
      if (panes.current)
        swap(panes.current, () => {
          if (!r.mounted || !panes.current) return [];
          flushSync(() => setShown(k));
          after();
          return [panes.current];
        });
    });
  return (
    <section className="rw-tk" id="rwTk" aria-label={ut('Задания')}>
      <Sticky className="rw-hd" id="rwHd">
        <Seg<TabKey>
          id="rwSeg"
          className="rw-seg"
          label={ut('Задания')}
          value={S.tab}
          onChange={onTab}
          dataKey="data-rw-tab"
          items={TABS.map(([k, l]) => ({
            key: k,
            id: 'rwTab-' + k,
            controls: 'rwP-' + k,
            label: (
              <span className="t">
                {ut(l)}
                <i className={'dot' + (r.c.claimN(k) ? ' on' : '')} aria-hidden="true" />
              </span>
            ),
          }))}
        />
      </Sticky>
      <div className="rw-panes" id="rwPanes" ref={panes}>
        <Pane k="day" on={shown === 'day'}>
          <DayPane />
        </Pane>
        <Pane k="one" on={shown === 'one'}>
          <OnePane />
        </Pane>
        <Pane k="fr" on={shown === 'fr'}>
          <FrPane />
        </Pane>
      </div>
      <p className="rw-fine">{ut('Награды приходят в Circle: ими можно платить в STEOS, выводить нельзя.')}</p>
    </section>
  );
}

/* ── Партнёрский блок ── */

function Partner() {
  const { c, BC, ui, store } = getRt();
  const TIERS = c.tiers();
  const b = c.buyersN();
  const ti = c.tierI(b);
  const t = TIERS[ti];
  const e = c.earnN();
  const nx = TIERS[ti + 1];
  /* без уровней с сервера блоку нечего показать — его нет, демо-уровни не подставляются */
  if (!t) return null;
  const buyersW = (n: number) => upl(n, 'покупатель', 'покупателя', 'покупателей');
  /* блок ссылки и промокода — общий из ядра (BotCore.ref.html): разметка ставится один раз, отметку «скопировано» он ведёт сам */
  const refHtml = () => BC.ref.html({ copied: store.S.copied });
  return (
    <section className="promo rw-pt" id="rwPt" aria-label={ut('Партнёрская программа')}>
      <div className="promo-h">
        <span className="promo-eb">
          <Ic n="partner" />
          {ut('Партнёрам')}
        </span>
        <span className="promo-n">
          {ut(t.name)} · {t.pct}%
        </span>
      </div>
      <h3 className="promo-t">{fmt('До {0}% {1}', BC.REF.max, <span>{ut('с покупок друзей')}</span>)}</h3>
      <p className="promo-d">
        {b
          ? fmt('Заработано {0} с покупок {1} {2}', <b>{BC.cNum(e) + NB}C</b>, b, upl(b, 'друга', 'друзей', 'друзей'))
          : ut('Сами выбирайте: больше возможностей в STEOS или доход с рекомендаций.')}
      </p>
      <div className="rw-lad" role="list" aria-label={ut('Уровни')}>
        {TIERS.map((x, i) => {
          const n = TIERS[i + 1];
          const f = i < ti ? 1 : i > ti ? 0 : n ? (b - x.from) / (n.from - x.from) : 1;
          return (
            <div
              key={x.name}
              className={'rw-lv' + (i === ti ? ' on' : '') + (i < ti ? ' got' : '')}
              role="listitem"
              aria-current={i === ti ? 'true' : undefined}
            >
              <b className="p">{x.pct}%</b>
              <span className="nm">{ut(x.name)}</span>
              <span className="bar">
                <i style={{ transform: `scaleX(${f.toFixed(3)})` }} />
              </span>
            </div>
          );
        })}
      </div>
      <div className="rw-lvp">
        <span>
          {nx
            ? fmt(
                'Ещё {0} до уровня {1}',
                <b>
                  {nx.from - b} {buyersW(nx.from - b)}
                </b>,
                ut(nx.name),
              )
            : ut('Максимальный уровень: вывод через STEOS ID')}
        </span>
        <span className="n">{nx ? uf('{0} из {1}', b, nx.from) : `${BC.cNum(b)} ${buyersW(b)}`}</span>
      </div>
      <LegacyHtml html={refHtml} />
      <button
        className="rw-ptgo"
        type="button"
        data-rw-partner=""
        onClick={() => {
          ui.buzz();
          ui.go('partner');
        }}
      >
        <span className="m">
          <b>{ut('Партнёрство')}</b>
          <span>{ut('Статистика, материалы и вывод')}</span>
        </span>
        <span className="rt">
          <Ic n="chev" />
        </span>
      </button>
    </section>
  );
}

/* ── Страница ── */

/* ── Сервер: загрузка, сбой, незавершённое начисление ── */

/** Заметка набора (BotCore.html.note) над серией: прежний запрос не подтверждён — «Проверить»; сбой при готовых данных — «Обновить» */
function SrvNote() {
  const { BC, store } = getRt();
  const v = store.S.srv;
  const ref = usePopOnAppear<HTMLDivElement>();
  /* pending ставится до запроса (защита от двойного начисления, rewards-server.ts) — пока запрос идёт (busy), заметки нет:
     иначе она выскакивала над серией на каждом «Забрать» и страница дёргалась вниз и обратно */
  const unresolved = v?.pending && !v.busy;
  if (!v?.ready || (!unresolved && !v.err)) return null;
  const html = unresolved
    ? BC.html.note(
        'warn',
        BC.esc(ut('Исход прежнего запроса награды нужно проверить. Новые начисления пока заблокированы')),
        '<button class="btn sm line" type="button" data-rw-retry>' + BC.esc(ut('Проверить')) + '</button>',
        'rw-note',
      )
    : BC.html.note(
        'bad',
        BC.esc(ut(v.err)),
        '<button class="btn sm line" type="button" data-rw-reload>' + BC.esc(ut('Обновить')) + '</button>',
        'rw-note',
      );
  return <div ref={ref} onClick={srvNoteTap} dangerouslySetInnerHTML={{ __html: html }} />;
}

function srvNoteTap(e: { target: EventTarget }) {
  const t = (e.target as Element).closest<HTMLElement>('[data-rw-retry],[data-rw-reload]');
  if (!t || t.classList.contains('busy')) return;
  if (t.dataset.rwRetry !== undefined) void srvRetry(t);
  else void window.SteosUI.busy(t, srvLoad);
}

/** Заглушка строки задания: значок, название и подпись, справа награда — та же строка .st-mr, блоки .skel вместо текста */
function SkTask() {
  return (
    <div className="st-mr rw-skm">
      <span className="ic skel" />
      <span className="m">
        <i className="skel" />
        <i className="skel" />
      </span>
      <i className="rw-skp skel" />
    </div>
  );
}

/** До первого снимка: разметка готовой страницы — карточка серии (шапка, заголовок, описание, неделя, кнопка), вкладки, заголовок и строки заданий —
    с блоками .skel вместо текста: снимок встаёт на место без скачка высоты; сбой без данных — пустое состояние набора с «Повторить» */
function SrvWait() {
  const { BC, store } = getRt();
  const v = store.S.srv!;
  if (!v.err)
    return (
      <div className="rw-sk" role="status" aria-label={ut('Загружаем награды')}>
        <div className="rw-skc" aria-hidden="true">
          <div className="rw-skh">
            <i className="skel" />
            <i className="skel" />
          </div>
          <i className="rw-skt skel" />
          <i className="rw-skd skel" />
          <div className="rw-skw">
            {Array.from({ length: 7 }, (_, i) => (
              <i key={i} className="skel" />
            ))}
          </div>
          <i className="rw-skb skel" />
        </div>
        <div className="rw-tk" aria-hidden="true">
          <i className="rw-sks skel" />
          <div className="st-h">
            <i className="rw-skl skel" />
            <i className="rw-skr skel" />
          </div>
          <div className="st-grp">
            {Array.from({ length: 4 }, (_, i) => (
              <SkTask key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  const html = BC.html.empty(
    BC.ic('warn'),
    BC.esc(ut('Награды не загрузились')),
    BC.esc(ut(v.err)),
    '<button class="btn sm line" type="button" data-rw-reload>' + BC.esc(ut('Повторить')) + '</button>',
    'rw-emp',
  );
  return <div onClick={srvNoteTap} dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Снимок на месте заглушек проявляется один раз (.ld-in: только opacity, без сдвига); класс снимается по окончании */
function ldIn(el: HTMLElement | null) {
  if (!el) return;
  el.classList.add('ld-in');
  const end = (e: AnimationEvent) => {
    if (e.target !== el) return;
    el.removeEventListener('animationend', end);
    el.classList.remove('ld-in');
  };
  el.addEventListener('animationend', end);
}

export function Rewards() {
  const r = getRt();
  useStore(r.store);
  /* смена языка профиля: страница перерисовывается целиком (подписи берутся из словаря при отрисовке) */
  useAccountLocale();
  const live = useLiveRoot();
  const v = r.store.S.srv;
  const col = useRef<HTMLDivElement>(null);
  /* [БЭКЕНД] заглушки были на экране (не сбой) — первый снимок после них проявляется; в демо (v нет) ничего не меняется */
  const skShown = useRef(false);
  const ready = v?.ready,
    err = v?.err;
  useLayoutEffect(() => {
    if (!v) return;
    if (!ready) {
      skShown.current = !err;
      return;
    }
    if (skShown.current) {
      skShown.current = false;
      ldIn(col.current);
    }
  }, [v, ready, err]);
  /* листы сторис и «STEOS ID» — общие из ядра, разметка ставится один раз */
  const sheets = () => r.BC.story.sheetHTML() + r.BC.sid.html();
  useLayoutEffect(() => {
    const f = requestAnimationFrame(() => r.mounted && window.SteosUI.syncGutters('#rewards'));
    return () => cancelAnimationFrame(f);
  }, [r]);
  return (
    <LiveContext.Provider value={live}>
      <div className="pg stp" id="rewards">
        <div className="stp-in" ref={col}>
          {v && !v.ready ? (
            <SrvWait />
          ) : (
            <>
              <SrvNote />
              <Streak />
              <Tasks />
              <Partner />
            </>
          )}
        </div>
        <LegacyHtml html={sheets} />
      </div>
    </LiveContext.Provider>
  );
}
